import { useThreadLogic } from "@features/Chat/hooks/useThreadLogic";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import PushPinIcon from "@mui/icons-material/PushPin";
import SupportIcon from "@mui/icons-material/Support";
import UndoIcon from "@mui/icons-material/Undo";
import { IconButton, Typography } from "@mui/material";
import { usePinThread } from "@queries/chat/usePinThread";
import {
  ActionSheetMenu,
  ActionSheetMenuItem,
  ResponsiveActionSheetMenu,
} from "@shared/components/ActionSheetMenu";
import { ThemeProvider } from "@shared/providers/ThemeProvider";
import { useIntercomIntegration } from "@shared/services/intercom/useIntercomIntegration";
import { useThreadsStore } from "@store/threads.store";
import { useUIStore } from "@store/ui.store";
import { useMemo } from "react";
import { useParams } from "react-router";

type ChatOverflowMenuProps = {
  onClose: () => void;
  showThreadActions: boolean;
  resolvedChatTitle: string;
  isPinned: boolean;
  pinLabel: string;
  onPinToggle: () => void;
  onDelete: () => void | Promise<void>;
  onOpenSupportHome: () => void;
};

const ChatOverflowMenu = (props: ChatOverflowMenuProps) => {
  const {
    onClose,
    showThreadActions,
    resolvedChatTitle,
    isPinned,
    pinLabel,
    onPinToggle,
    onDelete,
    onOpenSupportHome,
  } = props;

  return (
    <ActionSheetMenu
      header={
        showThreadActions
          ? { title: "Chat", subtitle: resolvedChatTitle }
          : undefined
      }
    >
      {showThreadActions && (
        <ActionSheetMenuItem
          icon={isPinned ? <UndoIcon /> : <PushPinIcon />}
          label={pinLabel}
          onClick={() => {
            onClose();
            onPinToggle();
          }}
        />
      )}
      {showThreadActions && (
        <ActionSheetMenuItem
          icon={<DeleteIcon />}
          label="Delete"
          onClick={async () => {
            onClose();
            await onDelete();
          }}
        />
      )}
      <ActionSheetMenuItem
        icon={<SupportIcon />}
        label="Support"
        onClick={() => {
          onClose();
          onOpenSupportHome();
        }}
      />
    </ActionSheetMenu>
  );
};

type Props = {
  iconColor?: string;
  buttonBgColor?: string;
  theme?: "dark" | "light";
};

export const HeaderMoreActions = ({
  iconColor = "inherit",
  buttonBgColor,
  theme,
}: Props) => {
  const intercom = useIntercomIntegration();
  const { threadId } = useParams<{ threadId: string }>();
  const toggleSupportDialogMode = useUIStore(
    (state) => state.toggleSupportDialogMode,
  );
  const confirmDialog = useUIStore((state) => state.confirmDialog);
  const threads = useThreadsStore((state) => state.threads);
  const updateThread = useThreadsStore((state) => state.updateThread);

  const thread = useMemo(
    () => threads.find((t) => t.id === threadId),
    [threads, threadId],
  );
  const isPinned = thread?.pinned ?? false;

  const pinOrUnpinThreadMutation = usePinThread();
  const { deleteThread } = useThreadLogic();

  const handlePinToggle = () => {
    if (!threadId) return;
    const nextPinned = !isPinned;
    updateThread(threadId, { pinned: nextPinned });
    pinOrUnpinThreadMutation.mutate({
      threadId,
      pinned: nextPinned,
    });
  };

  const handleDelete = async () => {
    if (!threadId) return;
    const response = await confirmDialog({
      title: "Delete chat?",
      content: (
        <Typography>
          This will delete <strong>{thread?.title}</strong>
        </Typography>
      ),
      confirmButtonText: "Delete",
    });

    if (!response) return;

    deleteThread(threadId);
  };

  const handleOpenSupportHome = () => {
    intercom.hide();
    toggleSupportDialogMode({ mode: "home" });
  };

  const showThreadActions = !!threadId;
  const resolvedChatTitle = (thread?.title?.trim() || "Untitled chat").trim();
  const pinLabel = isPinned ? "Unpin" : "Pin";

  const menuProps: Omit<ChatOverflowMenuProps, "onClose"> = {
    showThreadActions,
    resolvedChatTitle,
    isPinned,
    pinLabel,
    onPinToggle: handlePinToggle,
    onDelete: handleDelete,
    onOpenSupportHome: handleOpenSupportHome,
  };

  return (
    <ResponsiveActionSheetMenu
      renderTrigger={({ triggerRef, onClick }) => (
        <ThemeProvider defaultTheme={theme}>
          <IconButton
            id="thread-header-actions-button"
            ref={triggerRef}
            onClick={onClick}
            size="small"
            aria-label="Help and support options"
            sx={{
              borderRadius: "50%",
              backgroundColor: buttonBgColor,
            }}
          >
            <MoreHorizIcon sx={{ color: iconColor }} />
          </IconButton>
        </ThemeProvider>
      )}
    >
      {(closeMenu) => <ChatOverflowMenu {...menuProps} onClose={closeMenu} />}
    </ResponsiveActionSheetMenu>
  );
};
